import { useEffect, useState } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import { ArrowLeft, ExternalLink, MapPin } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { MAP_STYLE } from "./SearchMap";

const VenuePin = ({ coordinates, name }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;
    class Pin extends window.google.maps.OverlayView {
      onAdd() {
        this.node = document.createElement("div");
        this.node.className = "venue-map-pin";
        this.node.setAttribute("data-testid", "venue-map-marker");
        this.node.setAttribute("role", "img");
        this.node.setAttribute("aria-label", `Posizione di ${name}`);
        this.node.textContent = "●";
        this.getPanes().overlayMouseTarget.appendChild(this.node);
      }
      draw() {
        const point = this.getProjection()?.fromLatLngToDivPixel(new window.google.maps.LatLng(coordinates));
        if (point && this.node) { this.node.style.left = `${point.x}px`; this.node.style.top = `${point.y}px`; }
      }
      onRemove() { this.node?.remove(); }
    }
    const pin = new Pin(); pin.setMap(map);
    return () => pin.setMap(null);
  }, [map, coordinates.lat, coordinates.lng, name]);
  return null;
};

export const directionsUrl = (venue) => {
  const destination = venue?.address || (venue?.coordinates
    ? `${venue.coordinates.lat},${venue.coordinates.lng}` : null);
  if (!destination) return null;
  const query = new URLSearchParams({ api: "1", destination, travelmode: "driving" });
  if (venue?.place_id) query.set("destination_place_id", venue.place_id);
  return `https://www.google.com/maps/dir/?${query.toString()}`;
};

export const VenueMapView = ({ venue, onBack }) => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(""); setLocation(null);
    api.get(`/venues/${venue.id}/map`).then(({ data }) => { if (live) setLocation(data); })
      .catch((e) => { if (live) setError(errorMessage(e)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [venue.id, attempt]);

  const directions = directionsUrl(location || venue);
  return <div className="venue-map-view" data-testid="venue-map-view">
    <div className="venue-map-canvas" data-testid="venue-map-canvas">
      {location?.coordinates && location?.maps_key && !error ? <APIProvider apiKey={location.maps_key}
        onError={() => setError("Google Maps non è disponibile in questo momento.")}>
        <Map defaultCenter={location.coordinates} defaultZoom={16} styles={MAP_STYLE}
          disableDefaultUI zoomControl gestureHandling="greedy" clickableIcons={false}
          style={{ width: "100%", height: "100%" }}>
          <VenuePin coordinates={location.coordinates} name={venue.name} />
        </Map>
      </APIProvider> : <div className="venue-map-placeholder" data-testid="venue-map-placeholder">
        {loading ? <span role="status" data-testid="venue-map-loading">LOCALIZZAZIONE…</span>
          : <><MapPin size={35} aria-hidden="true" />
            <span role="alert" data-testid="venue-map-error">{error || "Non riusciamo a individuare con certezza questo locale sulla mappa."}</span>
            {error && <button type="button" className="text-action" onClick={() => setAttempt((value) => value + 1)} data-testid="venue-map-retry">Riprova</button>}</>}
      </div>}
    </div>
    <header className="venue-map-header">
      <button type="button" className="venue-map-back" onClick={onBack} data-testid="venue-map-back"><ArrowLeft size={20} aria-hidden="true" /> Scheda</button>
      <div className="venue-map-caption"><span className="section-label">LOCALE</span>
        <strong data-testid="venue-map-name">{venue.name}</strong></div>
    </header>
    <footer className="venue-map-footer">
      {venue.address && <span className="venue-map-address" data-testid="venue-map-address">{venue.address}</span>}
      {directions ? <a href={directions} target="_blank" rel="noopener noreferrer" className="hud-button venue-directions" data-testid="venue-directions-link"><ExternalLink size={18} aria-hidden="true" /> Indicazioni con Google Maps</a>
        : <span className="venue-map-no-directions" data-testid="venue-map-no-directions">Indirizzo non disponibile per la navigazione.</span>}
    </footer>
  </div>;
};